import Loader from '../../Loader';
import React, { Suspense, useEffect, useState } from 'react';
import { getFrontendProperties } from '../../../definitions/Config';
import FormDraftInfo from '../FormDraftInfo';
import Api from '../../../api/Api';
import Logger from '../../../utils/Logger';
import { Link, withRouter } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Alert,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material';
import { Print, Edit } from '@material-ui/icons';
import Dll from '@visma/formula/lib/dll';
import { getFormData } from '../../../data/questionnaires/actions';
import DataRow from './DataRow';
import Markdown from '@visma/formula/lib/Markdown';
import { FormattedMessage } from 'react-intl';
import sift from "sift";

const { id: module_id } = module;
const logger = Logger(module_id);

export const LAYER = 0;

const getSearchParams = () => {
  try {
    const paramObject = {};
    window.location.hash
      .toString()
      .split('?')[1]
      .split('&')
      .forEach((param) => {
        const split = param.split('=');
        paramObject[split[0]] = split[1];
      });
    return paramObject;
  } catch (e) {
    return {};
  }
};

const remapFormDataKeys = (elements, formData) => {
  const newFormData = {};

  for (const key in formData) {
    const element = elements.find((el) => el.id === key);
    if (key.startsWith('f_')) {
      newFormData[element.name] = formData[key];
    } else if (key.startsWith('fg_') && !element.multiValued) {
      newFormData[element.name] = remapFormDataKeys(
        element.elements,
        formData[key]
      );
    } else if (key.startsWith('fg_') && element.multiValued) {
      newFormData[element.name] = formData[key].map((data) =>
        remapFormDataKeys(element.elements, data)
      );
    }
  }

  return newFormData;
};

const getReviewElements = (elements) => {
  const reviewElements = [];
  elements.forEach((el) => {
    if (el.elements) {
      el.elements.forEach((el) => {
        if (el.type === 'summary') {
          reviewElements.push(el);
        }
      });
    } else if (el.type === 'summary') {
      reviewElements.push(el);
    }
  });
  return reviewElements;
};

const HighlightedReview = ({ successText, summaries }) => {
  return (
    <div className="successTextBox no-print-element">
      <Markdown className="successText">{successText}</Markdown>
      {summaries && (
        <>
          {summaries?.map((summary) => {
            try {
              return (
                <Markdown className="successText">
                  {JSON.parse(summary.content).fi}
                </Markdown>
              );
            } catch (e) {
              return <></>;
            }
          })}
        </>
      )}
    </div>
  );
};

const preFilterFormGroupElements = (element, formData) => {
  if (element.elements?.length > 0) {
    let mutatedFormGroupElement = {...element};
    let resultingMutatedElements = [];
    element.elements.forEach(el => {
      if (el.filter.length <= 2 && formData.values[element.name][el.name] && formData.values[element.name][el.name].length > 0) {

        resultingMutatedElements.push(el);

      } else {
        try {
          const filter = JSON.parse(el.filter);

          if (filter.show && sift(filter.show.query)(formData.values[element.name])) {
            resultingMutatedElements.push(el);
          }

          if (!filter.show) {
            resultingMutatedElements.push(el);
          }

        } catch (e) {
          console.error(`Couldn't parse filter data`);
        }
      }
    })

    if (resultingMutatedElements.length > 0) {
      mutatedFormGroupElement.elements = [...resultingMutatedElements];
      return mutatedFormGroupElement;
    } else {
      return element;
    }
  }
}

const orderAndPreFilterElements = (elements, formData) => {
  // Elements that should be rendered after filtering
  let resultingElements = [];

  // Sections (vaihe) with filtering are stored so that we can remove any questions related to the section
  // that do not include filtering of their own. If a section is filtered out, all related "children" should aswel.
  let sectionElementsWithFilter = [];

  elements.sort((el1, el2) => el1.orderNum - el2.orderNum);

  elements.forEach(element => {
    if (element.filter?.length > 2) {
      try {
        const filter = JSON.parse(element.filter);

        // If element has a filter but doesn't pass, we check if it is a section
        if (filter.show && !sift(filter.show.query)(formData.values)) {

          // Sections always start with pageTitle, so this is considered a "starting point" and used for filtering
          if (element.type === 'pageTitle') {
            // Store section starting point
            sectionElementsWithFilter.push(element);
          }

          // Other elements that do not pass and are not sections, DO NOT get added to returned result.
          // This means they are not rendered by default and need no further processing.

        } else {
          // Elements that have a filter and pass the check are added to the returned result.
          if (element.id.includes("fg_")) {
            const formGroupElement = preFilterFormGroupElements(element, formData);
            if (formGroupElement) {
              resultingElements.push(formGroupElement);
            }
          } else {
            resultingElements.push(element);
          }
        }

      } catch (e) {
        console.error(`Couldn't parse filter data`);
      }
    } else if (element.id.includes("fg_")) {

      const formGroupElement = preFilterFormGroupElements(element, formData);
      if (formGroupElement) {
        resultingElements.push(formGroupElement);
      }

    } else {
      resultingElements.push(element);
    }
  });

  // Remove all child elements from section if section is filtered out
  if (sectionElementsWithFilter.length > 0) {
    sectionElementsWithFilter.forEach(element => {

      // Index of starting point (a section that is being removed)
      const indexOfSectionTitle = elements.findIndex(el => el.orderNum === element.orderNum);
      // Index of the next section, so that we know the end index
      const indexOfNextSectionTitle = elements.findIndex(el => el.type === "pageTitle" && el.orderNum > element.orderNum);
      // Array of elements between the two indices that will be removed
      const elementsToBeRemoved = elements.slice(indexOfSectionTitle + 1, indexOfNextSectionTitle);

      elementsToBeRemoved.forEach(removable => {
        resultingElements = resultingElements.filter(resEl => resEl.orderNum !== removable.orderNum);
      });
    });
  }

  return resultingElements;
}

const FormDataViewer = (props) => {
  const assignmentId = props?.match?.params?.assignmentId;
  const [fetching, setFetching] = useState(true);
  const [formAssignment, setFormAssignment] = useState();
  const [hideNotAnswered, setHideNotAnswered] = useState(true);
  const [formData, setFormData] = useState({});
  const [formDefinition, setFormDefinition] = useState({});
  const [elements, setElements] = useState([]);
  const dispatch = useDispatch();
  const searchParams = getSearchParams();

  const getAssignedForms = async () => {
    try {
      const response = await Api.getAssignedForms();
      if (response) {
        setFormAssignment(
          response.find((obj) => {
            return obj.id === Number.parseInt(assignmentId);
          })
        );
      }
    } catch (error) {
      logger.error(error);
    }
  };
  useEffect(() => {
    getAssignedForms();
  }, []);

  useEffect(async () => {
    if (formAssignment !== undefined) {
      const fd = await Dll.useStandardizedForm(formAssignment.formulaFormId);
      const { data } = await dispatch(
        getFormData(formAssignment.formResponseId)
      );
      if (data?.values) {
        data.values = JSON.parse(data.values ?? '{}');
      }
      const remappedData = {
        ...data,
        values: remapFormDataKeys(
            fd.elements.sort((el1, el2) => el1.orderNum - el2.orderNum),
            data.values
        ),
      };

      setFormData(remappedData);
      setFormDefinition(fd);
      setElements(orderAndPreFilterElements(fd.elements, remappedData));
      setFetching(false);
    }
  }, [formAssignment]);

  const handleCheckboxChange = (event) => {
    setHideNotAnswered(event.target.checked);
  };

  const formulaFormIdWithoutRev = formAssignment?.formulaFormId?.split("_")[1];

  return (
    <Suspense fallback={<Loader />}>
      <div className="ot-mainview">
        <div aria-live="polite" className="otpro-content">
          {fetching ? (
            <Loader />
          ) : (
            <div className="otpro-questionnaire">
              <h1 className="print-h1">{formAssignment?.title}</h1>
              {getFrontendProperties().childScreenFormId === formulaFormIdWithoutRev && (
                  <div>
                    <p style={{fontSize: "1.6em"}}>Vointiarvion täyttämiseen pääset <a target="_blank" style={{color: "blue", fontWeight: "bold"}} href={"https://pientenpaivystys.pirha.fi/"}>tästä</a> linkistä.</p>
                    <br/>
                  </div>
                )
              }
              {searchParams?.showSubmitConfirmation && (
                <Alert className="no-print-element" severity="success">
                  Lomake lähetetty!
                </Alert>
              )}
              {formAssignment && !searchParams?.showSubmitConfirmation && (
                <FormDraftInfo form={formAssignment} />
              )}
              <FormGroup className="no-print-element">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={hideNotAnswered}
                      onChange={handleCheckboxChange}
                    />
                  }
                  label="Piilota vastaamattomat kysymykset"
                />
              </FormGroup>
              {searchParams?.showSubmitConfirmation &&
                formDefinition?.showSuccessTextOnTop &&
                (formDefinition?.messages?.fi?.successText ||
                  getReviewElements(elements).length > 0) && (
                  <HighlightedReview
                    successText={formDefinition?.messages?.fi?.successText}
                    summaries={getReviewElements(elements)}
                  />
                )}
              <TableContainer>
                <Table
                  sx={{ minWidth: 650 }}
                  className="print-fix-colors"
                  aria-label="simple table"
                >
                  <TableBody>
                    {elements.map((el, i) => (
                      <DataRow
                        element={el}
                        allElements={elements}
                        formData={formData.values[el.name]}
                        hideNotAnswered={hideNotAnswered}
                        fullFormData={formData.values}
                        useLayers={formDefinition.useLayers}
                        key={i}
                      />
                    ))}
                    <TableRow className="no-print-element">
                      <TableCell colSpan={2}>
                        <Button
                          style={{ maxWidth: 130 }}
                          variant="contained"
                          startIcon={<Print />}
                          onClick={() => setTimeout(() => global.print(), 0)}
                        >
                          <FormattedMessage id="print" />
                        </Button>
                        {formDefinition?.metadata?.allowEditAnswers && (
                          <Button
                            style={{
                              maxWidth: 130,
                              marginLeft: 10,
                              color: 'white',
                            }}
                            variant="contained"
                            startIcon={<Edit />}
                            component={Link}
                            to={`/questionnaires/response/${formAssignment.formulaFormId}/${formAssignment.formResponseId}`}
                          >
                            <FormattedMessage id="edit" />
                          </Button>
                        )}
                        <Button
                          style={{ maxWidth: 130, marginLeft: 10 }}
                          variant="outlined"
                          component={Link}
                          to="/questionnaires"
                        >
                          <FormattedMessage id="return" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              {searchParams?.showSubmitConfirmation &&
                !formDefinition?.showSuccessTextOnTop &&
                (formDefinition?.messages?.fi?.successText ||
                  getReviewElements(elements).length > 0) && (
                  <HighlightedReview
                    successText={formDefinition?.messages?.fi?.successText}
                    summaries={getReviewElements(elements)}
                  />
                )}
            </div>
          )}
        </div>
      </div>
    </Suspense>
  );
};

export default withRouter(FormDataViewer);
